import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { PreConsultationForm } from '../../services/preConsultationService';
import './PreConsultationForm.css';

interface PreConsultationFormComponentProps {
  form: PreConsultationForm;
  onSave: (formData: Partial<PreConsultationForm>) => void;
  onSubmit?: () => void;
  readOnly?: boolean;
  isNutritionist?: boolean;
}

export const PreConsultationFormComponent: React.FC<PreConsultationFormComponentProps> = ({
  form,
  onSave,
  onSubmit,
  readOnly = false,
  isNutritionist = false,
}) => {
  const [formData, setFormData] = useState<PreConsultationForm>(form);
  const [activeSection, setActiveSection] = useState<string>('personal');

  const updateField = (section: keyof PreConsultationForm, field: string, value: any) => {
    const updated = {
      ...formData,
      [section]: {
        ...(formData[section] as any),
        [field]: value,
      },
    };
    setFormData(updated);
  };

  const handleSave = () => {
    onSave(formData);
  };

  const sections = [
    { id: 'personal', label: '📋 Informações Pessoais', icon: '👤' },
    { id: 'health', label: '🏥 Saúde', icon: '💊' },
    { id: 'lifestyle', label: '🏃 Estilo de Vida', icon: '🌱' },
    { id: 'dietary', label: '🍽️ Hábitos Alimentares', icon: '🥗' },
    { id: 'goals', label: '🎯 Objetivos', icon: '✨' },
    { id: 'additional', label: '📝 Informações Adicionais', icon: '📄' },
  ];

  return (
    <div className="ns-preconsultation-form">
      <div className="ns-form-sections">
        {sections.map((section) => (
          <button
            key={section.id}
            type="button"
            className={`ns-section-tab ${activeSection === section.id ? 'active' : ''}`}
            onClick={() => setActiveSection(section.id)}
          >
            {section.label}
          </button>
        ))}
      </div>

      <div className="ns-form-content">
        {/* Informações Pessoais */}
        {activeSection === 'personal' && (
          <Card title="Informações Pessoais" subtitle="Dados básicos do paciente">
            <div className="ns-form-grid">
              <label className="ns-field">
                <span>Idade</span>
                <input
                  type="number"
                  className="ns-input"
                  value={formData.personalInfo?.age || ''}
                  onChange={(e) => updateField('personalInfo', 'age', parseInt(e.target.value) || undefined)}
                  disabled={readOnly}
                  min="0"
                  max="120"
                />
              </label>

              <label className="ns-field">
                <span>Gênero</span>
                <select
                  className="ns-input"
                  value={formData.personalInfo?.gender || ''}
                  onChange={(e) => updateField('personalInfo', 'gender', e.target.value || undefined)}
                  disabled={readOnly}
                >
                  <option value="">Selecione</option>
                  <option value="male">Masculino</option>
                  <option value="female">Feminino</option>
                  <option value="other">Outro</option>
                </select>
              </label>

              <label className="ns-field">
                <span>Altura (cm)</span>
                <input
                  type="number"
                  className="ns-input"
                  value={formData.personalInfo?.height || ''}
                  onChange={(e) => updateField('personalInfo', 'height', parseFloat(e.target.value) || undefined)}
                  disabled={readOnly}
                  min="50"
                  max="250"
                />
              </label>

              <label className="ns-field">
                <span>Peso (kg)</span>
                <input
                  type="number"
                  className="ns-input"
                  value={formData.personalInfo?.weight || ''}
                  onChange={(e) => updateField('personalInfo', 'weight', parseFloat(e.target.value) || undefined)}
                  disabled={readOnly}
                  min="0"
                  step="0.1"
                />
              </label>

              <label className="ns-field" style={{ gridColumn: '1 / -1' }}>
                <span>Ocupação</span>
                <input
                  type="text"
                  className="ns-input"
                  value={formData.personalInfo?.occupation || ''}
                  onChange={(e) => updateField('personalInfo', 'occupation', e.target.value)}
                  disabled={readOnly}
                  placeholder="Ex: Engenheiro, Estudante, etc."
                />
              </label>

              <label className="ns-field" style={{ gridColumn: '1 / -1' }}>
                <span>Telefone</span>
                <input
                  type="tel"
                  className="ns-input"
                  value={formData.personalInfo?.phone || ''}
                  onChange={(e) => updateField('personalInfo', 'phone', e.target.value)}
                  disabled={readOnly}
                  placeholder="(00) 00000-0000"
                />
              </label>
            </div>
          </Card>
        )}

        {/* Saúde */}
        {activeSection === 'health' && (
          <Card title="Informações de Saúde" subtitle="Histórico médico e condições de saúde">
            <div className="ns-form">
              <label className="ns-field">
                <span>Condições Médicas</span>
                <textarea
                  className="ns-input"
                  rows={4}
                  value={formData.healthInfo?.medicalConditions?.join(', ') || ''}
                  onChange={(e) => updateField('healthInfo', 'medicalConditions', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                  disabled={readOnly}
                  placeholder="Ex: Diabetes, Hipertensão, etc. (separados por vírgula)"
                />
              </label>

              <label className="ns-field">
                <span>Medicamentos em Uso</span>
                <textarea
                  className="ns-input"
                  rows={4}
                  value={formData.healthInfo?.medications?.join(', ') || ''}
                  onChange={(e) => updateField('healthInfo', 'medications', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                  disabled={readOnly}
                  placeholder="Liste os medicamentos que você toma regularmente"
                />
              </label>

              <label className="ns-field">
                <span>Alergias</span>
                <textarea
                  className="ns-input"
                  rows={3}
                  value={formData.healthInfo?.allergies?.join(', ') || ''}
                  onChange={(e) => updateField('healthInfo', 'allergies', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                  disabled={readOnly}
                  placeholder="Ex: Amendoim, Lactose, etc."
                />
              </label>

              <label className="ns-field">
                <span>Cirurgias Prévia</span>
                <textarea
                  className="ns-input"
                  rows={3}
                  value={formData.healthInfo?.surgeries?.join(', ') || ''}
                  onChange={(e) => updateField('healthInfo', 'surgeries', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                  disabled={readOnly}
                  placeholder="Liste cirurgias realizadas"
                />
              </label>

              <label className="ns-field">
                <span>Histórico Familiar</span>
                <textarea
                  className="ns-input"
                  rows={4}
                  value={formData.healthInfo?.familyHistory || ''}
                  onChange={(e) => updateField('healthInfo', 'familyHistory', e.target.value)}
                  disabled={readOnly}
                  placeholder="Doenças na família (diabetes, hipertensão, etc.)"
                />
              </label>
            </div>
          </Card>
        )}

        {/* Estilo de Vida */}
        {activeSection === 'lifestyle' && (
          <Card title="Estilo de Vida" subtitle="Atividades e hábitos diários">
            <div className="ns-form">
              <label className="ns-field">
                <span>Nível de Atividade</span>
                <select
                  className="ns-input"
                  value={formData.lifestyle?.activityLevel || ''}
                  onChange={(e) => updateField('lifestyle', 'activityLevel', e.target.value || undefined)}
                  disabled={readOnly}
                >
                  <option value="">Selecione</option>
                  <option value="sedentary">Sedentário (pouco ou nenhum exercício)</option>
                  <option value="light">Leve (exercício leve 1-3 dias/semana)</option>
                  <option value="moderate">Moderado (exercício moderado 3-5 dias/semana)</option>
                  <option value="active">Ativo (exercício intenso 6-7 dias/semana)</option>
                  <option value="very_active">Muito Ativo (exercício muito intenso, trabalho físico)</option>
                </select>
              </label>

              <label className="ns-field">
                <span>Frequência de Exercícios (vezes por semana)</span>
                <input
                  type="number"
                  className="ns-input"
                  value={formData.lifestyle?.exerciseFrequency || ''}
                  onChange={(e) => updateField('lifestyle', 'exerciseFrequency', parseInt(e.target.value) || undefined)}
                  disabled={readOnly}
                  min="0"
                  max="7"
                />
              </label>

              <label className="ns-field">
                <span>Horas de Sono por Noite</span>
                <input
                  type="number"
                  className="ns-input"
                  value={formData.lifestyle?.sleepHours || ''}
                  onChange={(e) => updateField('lifestyle', 'sleepHours', parseFloat(e.target.value) || undefined)}
                  disabled={readOnly}
                  min="0"
                  max="12"
                  step="0.5"
                />
              </label>

              <label className="ns-field">
                <span>Ingestão de Água (litros por dia)</span>
                <input
                  type="number"
                  className="ns-input"
                  value={formData.lifestyle?.waterIntake || ''}
                  onChange={(e) => updateField('lifestyle', 'waterIntake', parseFloat(e.target.value) || undefined)}
                  disabled={readOnly}
                  min="0"
                  max="10"
                  step="0.5"
                />
              </label>

              <label className="ns-field">
                <span>Fuma?</span>
                <select
                  className="ns-input"
                  value={formData.lifestyle?.smoking ? 'true' : formData.lifestyle?.smoking === false ? 'false' : ''}
                  onChange={(e) => updateField('lifestyle', 'smoking', e.target.value === 'true')}
                  disabled={readOnly}
                >
                  <option value="">Selecione</option>
                  <option value="true">Sim</option>
                  <option value="false">Não</option>
                </select>
              </label>

              <label className="ns-field">
                <span>Consumo de Álcool</span>
                <select
                  className="ns-input"
                  value={formData.lifestyle?.alcohol || ''}
                  onChange={(e) => updateField('lifestyle', 'alcohol', e.target.value || undefined)}
                  disabled={readOnly}
                >
                  <option value="">Selecione</option>
                  <option value="never">Nunca</option>
                  <option value="occasionally">Ocasionalmente</option>
                  <option value="regularly">Regularmente</option>
                </select>
              </label>
            </div>
          </Card>
        )}

        {/* Hábitos Alimentares */}
        {activeSection === 'dietary' && (
          <Card title="Hábitos Alimentares" subtitle="Informações sobre alimentação">
            <div className="ns-form">
              <label className="ns-field">
                <span>Refeições por Dia</span>
                <input
                  type="number"
                  className="ns-input"
                  value={formData.dietaryHabits?.mealsPerDay || ''}
                  onChange={(e) => updateField('dietaryHabits', 'mealsPerDay', parseInt(e.target.value) || undefined)}
                  disabled={readOnly}
                  min="1"
                  max="10"
                />
              </label>

              <label className="ns-field">
                <span>Frequência de Comer Fora</span>
                <select
                  className="ns-input"
                  value={formData.dietaryHabits?.eatingOutFrequency || ''}
                  onChange={(e) => updateField('dietaryHabits', 'eatingOutFrequency', e.target.value || undefined)}
                  disabled={readOnly}
                >
                  <option value="">Selecione</option>
                  <option value="never">Nunca</option>
                  <option value="sometimes">Às vezes</option>
                  <option value="often">Frequentemente</option>
                </select>
              </label>

              <label className="ns-field">
                <span>Cozinha em Casa?</span>
                <select
                  className="ns-input"
                  value={formData.dietaryHabits?.cookingAtHome ? 'true' : formData.dietaryHabits?.cookingAtHome === false ? 'false' : ''}
                  onChange={(e) => updateField('dietaryHabits', 'cookingAtHome', e.target.value === 'true')}
                  disabled={readOnly}
                >
                  <option value="">Selecione</option>
                  <option value="true">Sim</option>
                  <option value="false">Não</option>
                </select>
              </label>

              <label className="ns-field">
                <span>Restrições Alimentares</span>
                <textarea
                  className="ns-input"
                  rows={3}
                  value={formData.dietaryHabits?.dietaryRestrictions?.join(', ') || ''}
                  onChange={(e) => updateField('dietaryHabits', 'dietaryRestrictions', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                  disabled={readOnly}
                  placeholder="Ex: Vegano, Vegetariano, Sem Glúten, Sem Lactose, etc."
                />
              </label>

              <label className="ns-field">
                <span>Preferências Alimentares</span>
                <textarea
                  className="ns-input"
                  rows={3}
                  value={formData.dietaryHabits?.foodPreferences || ''}
                  onChange={(e) => updateField('dietaryHabits', 'foodPreferences', e.target.value)}
                  disabled={readOnly}
                  placeholder="Alimentos que você gosta ou prefere"
                />
              </label>

              <label className="ns-field">
                <span>Alimentos que Não Gosta</span>
                <textarea
                  className="ns-input"
                  rows={3}
                  value={formData.dietaryHabits?.dislikes?.join(', ') || ''}
                  onChange={(e) => updateField('dietaryHabits', 'dislikes', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                  disabled={readOnly}
                  placeholder="Liste alimentos que você não gosta"
                />
              </label>
            </div>
          </Card>
        )}

        {/* Objetivos */}
        {activeSection === 'goals' && (
          <Card title="Objetivos" subtitle="O que você deseja alcançar">
            <div className="ns-form">
              <label className="ns-field">
                <span>Objetivo Principal</span>
                <select
                  className="ns-input"
                  value={formData.goals?.primaryGoal || ''}
                  onChange={(e) => updateField('goals', 'primaryGoal', e.target.value || undefined)}
                  disabled={readOnly}
                >
                  <option value="">Selecione</option>
                  <option value="weight_loss">Perda de Peso</option>
                  <option value="weight_gain">Ganho de Peso</option>
                  <option value="muscle_gain">Ganho de Massa Muscular</option>
                  <option value="maintenance">Manutenção de Peso</option>
                  <option value="health">Melhorar Saúde Geral</option>
                  <option value="performance">Melhorar Performance Esportiva</option>
                </select>
              </label>

              <label className="ns-field">
                <span>Objetivos Específicos</span>
                <textarea
                  className="ns-input"
                  rows={4}
                  value={formData.goals?.specificGoals || ''}
                  onChange={(e) => updateField('goals', 'specificGoals', e.target.value)}
                  disabled={readOnly}
                  placeholder="Descreva seus objetivos de forma mais detalhada"
                />
              </label>

              <label className="ns-field">
                <span>Prazo/Timeline</span>
                <input
                  type="text"
                  className="ns-input"
                  value={formData.goals?.timeline || ''}
                  onChange={(e) => updateField('goals', 'timeline', e.target.value)}
                  disabled={readOnly}
                  placeholder="Ex: 3 meses, 6 meses, etc."
                />
              </label>

              <label className="ns-field">
                <span>Tentativas Anteriores</span>
                <textarea
                  className="ns-input"
                  rows={4}
                  value={formData.goals?.previousAttempts || ''}
                  onChange={(e) => updateField('goals', 'previousAttempts', e.target.value)}
                  disabled={readOnly}
                  placeholder="Descreva tentativas anteriores de dieta ou mudança de hábitos"
                />
              </label>
            </div>
          </Card>
        )}

        {/* Informações Adicionais */}
        {activeSection === 'additional' && (
          <Card title="Informações Adicionais" subtitle="Qualquer informação relevante">
            <div className="ns-form">
              <label className="ns-field">
                <span>Observações</span>
                <textarea
                  className="ns-input"
                  rows={8}
                  value={formData.additionalInfo || ''}
                  onChange={(e) => setFormData({ ...formData, additionalInfo: e.target.value })}
                  disabled={readOnly}
                  placeholder="Qualquer informação adicional que considere relevante para a consulta"
                />
              </label>
            </div>
          </Card>
        )}
      </div>

      {!readOnly && (
        <div className="ns-form-actions">
          <Button variant="outline" onClick={handleSave}>
            💾 Salvar Rascunho
          </Button>
          {onSubmit && (
            <Button onClick={onSubmit}>
              ✅ Submeter Formulário
            </Button>
          )}
        </div>
      )}

      {readOnly && isNutritionist && (
        <div className="ns-form-actions">
          <p style={{ color: 'var(--page-text-muted)', fontSize: '14px' }}>
            Status: {form.status === 'pending' ? '⏳ Aguardando Revisão' : '✅ Revisado'}
            {form.submittedAt && (
              <> • Submetido em {new Date(form.submittedAt).toLocaleString('pt-BR')}</>
            )}
          </p>
        </div>
      )}
    </div>
  );
};

